// Where the open booklet sits on screen. The DOM reader lays its pages out
// on this rect, and the stage flies the booklet onto the same rect before
// handing over, so the two must agree to the pixel.

// A booklet's width over its height.
export const BOOKLET_ASPECT = 0.75

// The open booklet sits a pixel inside the reader's pages all round, so
// none of it bleeds past their edges once they cover it.
export const READING_INSET = 1

// Vertical room kept clear under the booklet for the control dock (the
// booklet/3D toggle and the box button), so it never lands on the page.
const DOCK_SPACE = 84

// The page width a spread's type is drawn for (a 1440×900 window); a
// spread narrower than this shrinks --page-scale, and its body text with
// it (see readerStyle in Reader.tsx).
export const PAGE_DESIGN_W = 567
// The base body-text size at --page-scale: 1, and the smallest it may
// shrink to before a spread is unreadable and a single page reads better.
const BODY_PX = 13
const MIN_BODY_PX = 9

export interface Viewport {
  width: number
  height: number
  mobile: boolean
}

export function makeViewport(width: number, height: number): Viewport {
  return { width, height, mobile: width < 760 }
}

// A two-page spread, or a single page: on a phone portrait, or on any
// short, wide viewport (a phone turned sideways) where a spread's two
// pages would set body text under MIN_BODY_PX. Screen space, top-left
// origin; `hinge` is the spine's x from the screen's centre.
export function spreadRect(v: Viewport) {
  // The dock sits below the booklet, so lay it out in the viewport's
  // height less that band rather than the full height.
  const areaH = Math.max(0, v.height - DOCK_SPACE)
  const spreadPageW =
    Math.min(areaH * 0.84, (v.width * 0.94) / (2 * BOOKLET_ASPECT)) *
    BOOKLET_ASPECT
  const bodyPx = BODY_PX * Math.min(1.3, spreadPageW / PAGE_DESIGN_W)
  const single = v.mobile || bodyPx < MIN_BODY_PX
  // Landscape leaves little height to spare, so a single page there uses
  // most of it rather than the portrait crop's wide top/bottom margins.
  const landscape = v.width > v.height
  const pageW = single ? v.width * 0.92 : spreadPageW
  const pageH = single
    ? Math.min(areaH * (landscape ? 0.92 : 0.72), pageW * 1.6)
    : pageW / BOOKLET_ASPECT
  const top = (areaH - pageH) / 2 + areaH * (single ? -0.01 : 0.015)
  const left = single ? (v.width - pageW) / 2 : v.width / 2 - pageW
  return {
    single,
    pageW,
    pageH,
    left,
    top,
    width: single ? pageW : pageW * 2,
    height: pageH,
    hinge: single ? -pageW / 2 : 0,
  }
}
